import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api, getSlugFromHostname } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { ExcelSheetViewer, type ExcelLayout, type ExcelField as ViewerField } from './ExcelSheetViewer';

interface ExcelField {
  id: string;
  cell_ref: string;
  merge_range?: string | null;
  label: string;
  sub_label?: string;
  sheet_name?: string;
  field_type: string;
  required: boolean;
  order?: number;
}

interface ExcelTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  sheet_name?: string;
  fields: ExcelField[];
  mappings: Array<{
    field_id: string;
    source_table: string;
    source_column: string;
  }>;
}

// cell_ref "Sheet1!A1" or "A1" → シート名を抽出
function extractSheetName(cellRef: string): string {
  return cellRef.includes('!') ? cellRef.split('!')[0] : '';
}

// template の全シート名を取得（fields の cell_ref から抽出 + sheet_name カラム併用）
function extractSheetNames(template: ExcelTemplate): string[] {
  const set = new Set<string>();
  for (const f of template.fields || []) {
    const s = extractSheetName(f.cell_ref);
    if (s) set.add(s);
  }
  // sheet_name カラムもカンマ区切りで追加
  if (template.sheet_name) {
    for (const s of template.sheet_name.split(',').map(x => x.trim()).filter(Boolean)) {
      set.add(s);
    }
  }
  return Array.from(set);
}

interface Property { id: string; name: string; address?: string; }
interface Customer { id: string; name: string; }

interface Props {
  open: boolean;
  onClose: () => void;
  properties: Property[];
  customers: Customer[];
  dealId?: string;
  contractId?: string;
  submissionId?: string;
  initialPropertyId?: string;
  initialCustomerId?: string;
  propertyName?: string;
  customerName?: string;
  initialCategory?: string;
  existingDocId?: string;
}

const CATEGORIES: Record<string, string> = {
  important_explanation: '重要事項説明書',
  subsidy_application: '助成金申請書',
  lease: '賃貸借契約書',
  sales: '売買契約書',
  management: '管理委託契約書',
  notification: '届出書',
  other: 'その他',
};

export function ExcelDocumentModal({ open, onClose, properties, customers, dealId, contractId, submissionId, initialPropertyId, initialCustomerId, propertyName, customerName, initialCategory, existingDocId }: Props) {
  const { data: templates } = useApi<ExcelTemplate[]>(open ? '/tenant/excel-templates' : '');

  const [step, setStep] = useState<'select' | 'fill' | 'preview'>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<ExcelTemplate | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [docId, setDocId] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [resolvedValues, setResolvedValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState('document.pdf');
  const [activeSheet, setActiveSheet] = useState('');
  const [templateId, setTemplateId] = useState<string>('');
  const [layouts, setLayouts] = useState<Record<string, ExcelLayout | null>>({});
  const [layoutLoading, setLayoutLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // 取引から開いた場合、物件・顧客は固定
  const isFromDeal = !!dealId;

  // リセット
  useEffect(() => {
    if (!open) {
      setStep('select');
      setSelectedTemplate(null);
      setPropertyId('');
      setCustomerId('');
      setDocId(null);
      setFieldValues({});
      setResolvedValues({});
      setActiveSheet('');
      setTemplateId('');
      setLayouts({});
      if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); }
    } else {
      // 初期値セット
      if (initialPropertyId) setPropertyId(initialPropertyId);
      if (initialCustomerId) setCustomerId(initialCustomerId);
    }
  }, [open]);

  // 既存ドラフト読み込み
  useEffect(() => {
    if (!open || !existingDocId) return;
    let cancelled = false;
    (async () => {
      try {
        const doc = await api<{
          id: string;
          template_id: string;
          field_values: Record<string, string> | null;
          resolved_values: Record<string, string> | null;
          template_fields: ExcelTemplate['fields'];
          template_mappings: ExcelTemplate['mappings'];
          template_name: string;
          category: string;
        }>(`/tenant/excel-documents/${existingDocId}`);
        if (cancelled) return;
        setDocId(doc.id);
        setTemplateId(doc.template_id);
        setFieldValues(doc.field_values || {});
        setResolvedValues(doc.resolved_values || {});
        setSelectedTemplate({
          id: doc.template_id,
          name: doc.template_name,
          description: '',
          category: doc.category,
          fields: doc.template_fields || [],
          mappings: doc.template_mappings || [],
        });
        setStep('fill');
      } catch {
        toast('下書きの読み込みに失敗しました', 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [open, existingDocId]);

  // activeSheet 初期化（docId + selectedTemplate が揃ったとき）
  useEffect(() => {
    if (!docId || !selectedTemplate) return;
    if (activeSheet) return;
    const sheets = extractSheetNames(selectedTemplate);
    if (sheets.length > 0) setActiveSheet(sheets[0]);
    else setActiveSheet('_default');
  }, [docId, selectedTemplate, activeSheet]);

  // layout fetch（activeSheet変更時）
  useEffect(() => {
    if (!docId || !templateId || !activeSheet) return;
    if (layouts[activeSheet]) return; // キャッシュ済みならスキップ
    let cancelled = false;
    setLayoutLoading(true);
    (async () => {
      try {
        const sheetParam = activeSheet === '_default' ? '' : activeSheet;
        const query = sheetParam ? `?sheet=${encodeURIComponent(sheetParam)}` : '';
        const layout = await api<ExcelLayout>(`/tenant/excel-templates/${templateId}/layout${query}`);
        if (cancelled) return;
        setLayouts(prev => ({ ...prev, [activeSheet]: layout }));
      } catch {
        toast('レイアウトの読み込みに失敗しました', 'error');
      } finally {
        if (!cancelled) setLayoutLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [docId, templateId, activeSheet]);

  // テンプレート選択→書類作成
  const handleSelect = useCallback(async (tpl: ExcelTemplate) => {
    setSelectedTemplate(tpl);
    setStep('fill');
  }, []);

  // 書類作成（DB注入）
  const handleCreateDoc = useCallback(async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    try {
      const result = await api<{ id: string; resolved_values: Record<string, string> }>(
        '/tenant/excel-documents',
        {
          method: 'POST',
          body: JSON.stringify({
            template_id: selectedTemplate.id,
            property_id: propertyId || undefined,
            customer_id: customerId || undefined,
            contract_id: contractId || undefined,
            deal_id: dealId || undefined,
            submission_id: submissionId || undefined,
          }),
        }
      );
      setDocId(result.id);
      setTemplateId(selectedTemplate.id);
      setResolvedValues(result.resolved_values || {});
      // 取引経由の場合、対応する契約ステータスを「確認中」に自動更新
      if (contractId) {
        try {
          await api(`/tenant/contracts/${contractId}`, { method: 'PATCH', body: JSON.stringify({ status: 'pending' }) });
        } catch { /* ignore */ }
      }
      toast('書類を作成しました。入力値を確認してください。');
    } catch (e: any) {
      toast(e.message || '書類作成に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedTemplate, propertyId, customerId]);

  // チェックボックス値を Excel表示用に変換（"1"→"☑", "0"→"☐"）
  const convertCheckboxValues = useCallback((values: Record<string, string>): Record<string, string> => {
    if (!selectedTemplate) return values;
    const out: Record<string, string> = {};
    for (const f of selectedTemplate.fields || []) {
      const v = values[f.id];
      if (v === undefined) continue;
      if (f.field_type === 'checkbox') {
        out[f.id] = v === '1' ? '☑' : v === '0' ? '☐' : v;
      } else {
        out[f.id] = v;
      }
    }
    return out;
  }, [selectedTemplate]);

  // 一時保存
  const handleSave = useCallback(async () => {
    if (!docId) return;
    setSaving(true);
    try {
      await api('/tenant/excel-documents/' + docId, {
        method: 'PATCH',
        body: JSON.stringify({ field_values: fieldValues }),
      });
      toast('一時保存しました');
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSaving(false); }
  }, [docId, fieldValues]);

  // PDFプレビュー生成
  const handlePreviewPdf = useCallback(async () => {
    if (!docId) return;
    setGenerating(true);
    try {
      // 手動入力値を先に保存（チェックボックスは☑/☐に変換してPDF注入用）
      await api('/tenant/excel-documents/' + docId, {
        method: 'PATCH',
        body: JSON.stringify({ field_values: convertCheckboxValues(fieldValues) }),
      });
      const result = await api<{ success: boolean; pdf_size: number }>(
        `/tenant/excel-documents/${docId}/generate-pdf`,
        { method: 'POST' }
      );
      if (result.success) {
        // PDFダウンロード→Blob URL生成
        const base = import.meta.env.VITE_API_URL || '/api';
        const { token, user } = useAuthStore.getState();
        const slug = getSlugFromHostname() || user?.tenantSlug || '';
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
        if (slug) headers['X-Tenant-Slug'] = slug;
        const pdfRes = await fetch(`${base}/tenant/excel-documents/${docId}/download-pdf`, { headers });
        if (pdfRes.ok) {
          const blob = await pdfRes.blob();
          const url = URL.createObjectURL(blob);
          // ファイル名取得
          const cd = pdfRes.headers.get('content-disposition') || '';
          const fnMatch = cd.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/i);
          setPdfFileName(fnMatch ? decodeURIComponent(fnMatch[1]) : 'document.pdf');
          if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
          setPdfBlobUrl(url);
          setStep('preview');
        } else {
          toast('PDFの取得に失敗しました', 'error');
        }
      }
    } catch (e: any) {
      toast(e.message || 'PDF生成に失敗しました', 'error');
    } finally {
      setGenerating(false);
    }
  }, [docId, fieldValues, pdfBlobUrl, convertCheckboxValues]);

  // 印刷
  const handlePrint = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.print();
    }
  }, []);

  // PDF保存（ローカルダウンロード）
  const handleDownload = useCallback(() => {
    if (!pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl;
    a.download = pdfFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [pdfBlobUrl, pdfFileName]);

  // マッピング済みフィールドかどうか
  const isMapped = (fieldId: string) => {
    return selectedTemplate?.mappings?.some(m => m.field_id === fieldId) ?? false;
  };

  // フッター
  const renderFooter = () => {
    if (step === 'fill' && docId) {
      return (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => { if (existingDocId) { onClose(); } else { setStep('select'); setDocId(null); setSelectedTemplate(null); } }}
            style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'none', color: 'var(--ink2)', cursor: 'pointer' }}>
            {existingDocId ? '閉じる' : '戻る'}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--ink1)', cursor: 'pointer' }}>
            {saving ? '保存中...' : '一時保存'}
          </button>
          <button onClick={handlePreviewPdf} disabled={generating}
            style={{ padding: '6px 14px', border: 'none', borderRadius: 6, background: 'var(--primary, #0ea5e9)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            {generating ? 'PDF生成中...' : 'PDFプレビュー'}
          </button>
        </div>
      );
    }
    if (step === 'fill' && !docId) {
      return (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => setStep('select')}
            style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'none', color: 'var(--ink2)', cursor: 'pointer' }}>
            戻る
          </button>
          <button onClick={handleCreateDoc} disabled={saving}
            style={{ padding: '6px 14px', border: 'none', borderRadius: 6, background: 'var(--primary, #0ea5e9)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            {saving ? '作成中...' : 'データ注入して作成'}
          </button>
        </div>
      );
    }
    if (step === 'preview') {
      return (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => setStep('fill')}
            style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'none', color: 'var(--ink2)', cursor: 'pointer' }}>
            ← 入力に戻る
          </button>
          <button onClick={handlePrint}
            style={{ padding: '6px 14px', border: 'none', borderRadius: 6, background: 'var(--primary, #0ea5e9)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
            印刷/PDF保存
          </button>
        </div>
      );
    }
    return undefined;
  };

  return (
    <Modal open={open} onClose={onClose} title="📊 Excel帳票" width={step === 'fill' ? 1200 : 700} fullscreen={step === 'preview'} footer={renderFooter()}>
      {step === 'select' && (
        <>
          <p style={{ color: 'var(--ink2)', fontSize: 13, marginBottom: 12 }}>
            公開済みのExcelテンプレートを選択してください。
          </p>
          {(() => {
            const filtered = initialCategory
              ? templates?.filter(t => t.category === initialCategory)
              : templates;
            return !filtered?.length ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--ink3)' }}>
              {initialCategory ? `${CATEGORIES[initialCategory] || initialCategory}のExcelテンプレートがありません` : '公開済みのExcelテンプレートがありません'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(tpl => (
                <div key={tpl.id} onClick={() => handleSelect(tpl)}
                  style={{
                    padding: '12px 16px', background: 'var(--surface2)', border: '1px solid var(--border)',
                    borderRadius: 8, cursor: 'pointer', transition: 'border-color 0.12s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--border2)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: 'var(--ink1)' }}>{tpl.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink3)', background: 'var(--surface3)', padding: '1px 6px', borderRadius: 4 }}>
                      {CATEGORIES[tpl.category] || tpl.category}
                    </span>
                  </div>
                  {tpl.description && <div style={{ fontSize: 12, color: 'var(--ink2)' }}>{tpl.description}</div>}
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
                    フィールド: {tpl.fields?.length || 0}個
                  </div>
                </div>
              ))}
            </div>
          );
          })()}
        </>
      )}

      {step === 'fill' && selectedTemplate && (
        <>
          <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <strong style={{ color: 'var(--ink1)' }}>{selectedTemplate.name}</strong>
            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink3)' }}>
              {CATEGORIES[selectedTemplate.category] || selectedTemplate.category}
            </span>
          </div>

          {!docId && (
            <>
              {/* 物件・顧客: 取引から開いた場合はラベル表示、それ以外はセレクト */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', display: 'block', marginBottom: 4 }}>物件</label>
                  {isFromDeal ? (
                    <div style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--ink1)', background: 'var(--surface2)' }}>
                      {propertyName || '未選択'}
                    </div>
                  ) : (
                    <select value={propertyId} onChange={e => setPropertyId(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--ink1)', background: 'var(--surface)' }}>
                      <option value="">選択なし</option>
                      {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', display: 'block', marginBottom: 4 }}>顧客</label>
                  {isFromDeal ? (
                    <div style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--ink1)', background: 'var(--surface2)' }}>
                      {customerName || '未選択'}
                    </div>
                  ) : (
                    <select value={customerId} onChange={e => setCustomerId(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, color: 'var(--ink1)', background: 'var(--surface)' }}>
                      <option value="">選択なし</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </>
          )}

          {docId && (() => {
            const sheetNames = extractSheetNames(selectedTemplate);
            const currentSheet = activeSheet || sheetNames[0] || '_default';
            const currentLayout = layouts[currentSheet];
            const viewerFields: ViewerField[] = (selectedTemplate.fields || []).filter(f => {
              // 現在のシートに属するフィールドのみ（シート名なしは全表示）
              if (!sheetNames.length) return true;
              const fs = extractSheetName(f.cell_ref);
              return fs === currentSheet;
            });

            return (
              <div>
                {/* シートタブ（複数シート時のみ） */}
                {sheetNames.length > 1 && (
                  <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border)', marginBottom: 12 }}>
                    {sheetNames.map(sn => (
                      <button key={sn} onClick={() => setActiveSheet(sn)}
                        style={{
                          padding: '6px 16px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                          borderBottom: currentSheet === sn ? '2px solid var(--primary, #0ea5e9)' : '2px solid transparent',
                          background: currentSheet === sn ? 'var(--surface)' : 'var(--surface2)',
                          color: currentSheet === sn ? 'var(--primary, #0ea5e9)' : 'var(--ink3)',
                          marginBottom: -2,
                        }}>
                        {sn}
                      </button>
                    ))}
                  </div>
                )}

                {/* Excel実レイアウトビューア */}
                {currentLayout ? (
                  <ExcelSheetViewer
                    layout={currentLayout}
                    fields={viewerFields}
                    fieldValues={fieldValues}
                    resolvedValues={resolvedValues}
                    onFieldChange={(fieldId, value) => setFieldValues(prev => ({ ...prev, [fieldId]: value }))}
                  />
                ) : (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink3)' }}>
                    {layoutLoading ? 'レイアウトを読み込み中...' : 'レイアウトがありません'}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {step === 'preview' && pdfBlobUrl && (
        <iframe
          ref={iframeRef}
          src={`${pdfBlobUrl}#zoom=100`}
          style={{ width: '100%', height: 'calc(95vh - 130px)', border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
          title="PDFプレビュー"
        />
      )}
    </Modal>
  );
}
